import classNames from "classnames";
import { ButtonNode, NodeStates } from "../../build/types";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { EditableInputProps } from "../BuilderInterface";
import DynamicIcon from "../DynamicIcon";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates
} from "../helpers";
import "./builder-editable-label.scss";

const BuilderEditableLabel: React.FC<EditableInputProps> = ({
    node,
    actionCallbacks,
    renderProps,
    builderProps,
    disabled
}: EditableInputProps) => {
    const { id, attr, stateManager, text } = node;
    const { setElement, states } = useInteractionStates<HTMLDivElement>();
    const bNode = node as ButtonNode;
    const editableLabelClass = classNames("builder-editable-label-wrapper", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        return (
            <div
                {...attr}
                id={id}
                className={editableLabelClass}
                style={getCompoundStyle({
                    node,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {bNode.icon?.src && (
                    <DynamicIcon
                        name={bNode.icon?.src}
                        style={{
                            ...bNode.icon?.style,
                            pointerEvents: "none",
                            ...getStateStyleForBuilder(stateManager, nodeState, "icon_style")
                        }}
                        colors={getStateColorsForBuilder(stateManager, nodeState, "icon_colors") || bNode.icon.colors}
                    />
                )}
                {text}
            </div>
        );
    } else {
        const { style, ...rest } = attr || {};

        const buttonStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const iconStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: bNode.icon?.style,
            stateManager,
            paths: ["icon_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const iconColors = getColorsFromInteractiveStates({
            states,
            stateManager,
            defaultColors: bNode.icon?.colors,
            paths: ["icon_colors"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const handleClick = () => {
            renderProps?.editingCallback?.(true);
        };

        const handleIconClick = () => {
            renderProps?.editingCallback?.(true);
        };

        const handleInputBlur = () => {
            renderProps?.editingCallback?.(false);
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            renderProps?.callback?.(e.target.value);
        };

        const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            e.target.select();
        };

        return (
            <div ref={setElement} id={id} {...rest} {...actionCallbacks} style={buttonStyle} className={editableLabelClass}>
                {bNode.icon?.src && (
                    <div onClick={handleIconClick}>
                        <DynamicIcon name={bNode.icon?.src} style={iconStyle} colors={iconColors} />
                    </div>
                )}
                {renderProps?.isEditing ? (
                    <input
                        style={{
                            border: "none",
                            width: "40%",
                            padding: 0,
                            margin: 0,
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            color: "inherit",
                            background: "transparent"
                        }}
                        type="text"
                        value={renderProps?.text}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onFocus={handleInputFocus}
                        autoFocus
                    />
                ) : (
                    <span
                        style={{
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            color: "inherit"
                        }}
                        onClick={handleClick}
                    >
                        {renderProps?.text || text}
                    </span>
                )}
            </div>
        );
    }
};

export default BuilderEditableLabel;
